import { APP_POOL_CONFIG } from '@synergy/config';
import { Field, FieldMetaProps, FieldProps, useFormikContext } from 'formik';
import { FormControlLabel, FormHelperText, Grid, Switch, Theme } from '@mui/material';
import { FormOfAddress } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolBanner } from '@synergy/components-next';
import { PoolSelect, PoolTextField, SelectOption } from '@synergy/components';
import { createStyles, makeStyles } from '@mui/styles';
import { fields } from '@synergy/utils';
import { useEffect, useState } from 'react';

import { EmployeeFormikValues } from './models/employee-formik-values';
import { PoolPositionAutocompleteList } from '../../common/autocomplete-list/position-autocomplete-list/pool-position-autocomplete-list';
import { localizedPositionsSelector } from '../../common/selectors/positions-selectors';
import { useSelector } from '../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(5),
        },
        formControlLabel: {
            '& .MuiTypography-root': {
                borderBottom: 'none',
                paddingBottom: 0,
                // FIXME: should be done in the theme or as a variant of the component
                fontSize: 18,
                fontWeight: 600,
                lineHeight: '130%',
            },
        },
    })
);

export const UserManagementDetailForm = () => {
    const classes = useStyles();
    const isExistingEmail = useSelector(state => state.userManagement.isExistingEmail);
    const positions = useSelector(localizedPositionsSelector);
    const fieldNames = fields<EmployeeFormikValues>();
    const formikContext = useFormikContext<EmployeeFormikValues>();
    const [showPositionHint, setShowPositionHint] = useState(formikContext.values.positionId === null);

    useEffect(() => {
        if (formikContext.values.positionId !== null) {
            setShowPositionHint(false);
        }
    }, [formikContext.values.positionId]);

    const formOfAddressSelectOptions: SelectOption[] = [
        { value: FormOfAddress.Mr, displayValue: <FormattedMessage id="app.common.mr" /> },
        { value: FormOfAddress.Mrs, displayValue: <FormattedMessage id="app.common.mrs" /> },
        {
            value: FormOfAddress.Neutral,
            displayValue: <FormattedMessage id="app.common.neutralName" />,
        },
    ];

    const getEmailHelperText = (meta: FieldMetaProps<string>) => {
        if (meta.touched && meta.error) {
            return <FormattedMessage id={meta.error} />;
        }
        if (isExistingEmail) {
            return (
                <FormattedMessage
                    id="app.error.existingEmail"
                    values={{ poolConfigNameShort: APP_POOL_CONFIG['name_short'] }}
                />
            );
        }
        return null;
    };

    return (
        <>
            <Grid container={true} spacing={2} className={classes.container}>
                <Grid item={true} xs={4}>
                    <Field name={fieldNames.formOfAddress}>
                        {({ field, form, meta }: FieldProps<FormOfAddress>) => (
                            <>
                                <PoolSelect
                                    {...field}
                                    fullWidth={true}
                                    options={formOfAddressSelectOptions}
                                    value={field.value?.toString()}
                                    onValueChange={value => form.setFieldValue(field.name, value)}
                                    name={field.name}
                                    label={<FormattedMessage id="app.common.formOfAddress" />}
                                    required={true}
                                    noSorting={true}
                                    disallowDefaultValue={true}
                                    error={meta.touched && !!meta.error}
                                />
                                {meta.error && meta.touched && (
                                    <FormHelperText error={true}>{<FormattedMessage id={meta.error} />}</FormHelperText>
                                )}
                            </>
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={8}>
                    <Field name={fieldNames.positionId}>
                        {({ field, form, meta }: FieldProps<number>) => (
                            <>
                                <PoolPositionAutocompleteList
                                    name={field.name}
                                    label={<FormattedMessage id="app.common.position" />}
                                    required={true}
                                    value={positions.find(position => position.id === field.value)}
                                    onSelected={value => form.setFieldValue(field.name, value?.id ?? null)}
                                    error={meta.touched && !!meta.error}
                                />
                                {meta.error && meta.touched && (
                                    <FormHelperText error={true}>{<FormattedMessage id={meta.error} />}</FormHelperText>
                                )}
                            </>
                        )}
                    </Field>
                </Grid>
                {showPositionHint && (
                    <Grid item={true} xs={12}>
                        <PoolBanner onClose={() => setShowPositionHint(false)}>
                            <FormattedMessage
                                id="app.employee.position.hint"
                                values={{ poolConfigNameShort: APP_POOL_CONFIG['name_short'] }}
                            />
                        </PoolBanner>
                    </Grid>
                )}
                <Grid item={true} xs={6}>
                    <Field name={fieldNames.lastName}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                required={true}
                                fullWidth={true}
                                label={<FormattedMessage id="app.table.column.lastName" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={6}>
                    <Field name={fieldNames.firstName}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                fullWidth={true}
                                required={true}
                                label={<FormattedMessage id="app.table.column.firstName" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={12}>
                    <Field name={fieldNames.phone}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                required={true}
                                fullWidth={true}
                                label={<FormattedMessage id="app.table.column.phone" />}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={12}>
                    <Field name={fieldNames.email}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                fullWidth={true}
                                label={<FormattedMessage id="app.table.column.email" />}
                                error={(meta.touched && !!meta.error) || isExistingEmail}
                                required={true}
                                helperText={getEmailHelperText(meta)}
                            />
                        )}
                    </Field>
                </Grid>
            </Grid>

            <Grid container={true} style={{ marginBottom: '0.5rem' }}>
                <Grid item={true} xs={6}>
                    <Field name={fieldNames.isUser}>
                        {({ field, form }: FieldProps<boolean>) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        id="switch"
                                        checked={field.value}
                                        onChange={() => form.setFieldValue(field.name, !field.value, false)}
                                        color="primary"
                                        disabled={form.values.isUser}
                                    />
                                }
                                label={<FormattedMessage id="app.common.login" />}
                                className={classes.formControlLabel}
                            />
                        )}
                    </Field>
                </Grid>
            </Grid>
        </>
    );
};
